import { useCallback, useEffect, useRef } from "react";
import useIntersectionObserver from "./useIntersectionObserver";

type UseBrowseInfiniteScrollArgs = {
  enabled: boolean;
  hasMore: boolean;
  isBusy: boolean;
  rootMarginPx: number;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  sentinelRef: React.RefObject<HTMLDivElement>;
  onIntersect: () => void;
};

const useBrowseInfiniteScroll = ({
  enabled,
  hasMore,
  isBusy,
  rootMarginPx,
  scrollContainerRef,
  sentinelRef,
  onIntersect,
}: UseBrowseInfiniteScrollArgs) => {
  const isEnabled = enabled && hasMore;
  const isBusyRef = useRef(isBusy);
  const onIntersectRef = useRef(onIntersect);
  const hasTriggeredRef = useRef(false);
  const isScrollableRoot = useCallback(() => {
    const rootElement = scrollContainerRef.current;
    if (!rootElement) return false;
    return rootElement.scrollHeight > rootElement.clientHeight + 1;
  }, [scrollContainerRef]);
  const getRoot = useCallback(() => {
    return isScrollableRoot() ? scrollContainerRef.current : null;
  }, [isScrollableRoot, scrollContainerRef]);

  useEffect(() => {
    if (!enabled || !hasMore) {
      hasTriggeredRef.current = false;
    }
  }, [enabled, hasMore]);

  useEffect(() => {
    isBusyRef.current = isBusy;
  }, [isBusy]);

  useEffect(() => {
    onIntersectRef.current = onIntersect;
  }, [onIntersect]);

  const handleIntersect = useCallback(() => {
    if (isBusyRef.current) return;
    const requireLeave = isScrollableRoot();
    if (requireLeave && hasTriggeredRef.current) return;
    if (requireLeave) {
      hasTriggeredRef.current = true;
    }
    onIntersectRef.current();
  }, [isScrollableRoot]);

  const handleLeave = useCallback(() => {
    hasTriggeredRef.current = false;
  }, []);

  useIntersectionObserver({
    enabled: isEnabled,
    targetRef: sentinelRef,
    root: getRoot,
    rootMargin: `${rootMarginPx}px`,
    threshold: 0.01,
    onIntersect: handleIntersect,
    onLeave: handleLeave,
  });
};

export default useBrowseInfiniteScroll;
